/**
 * Shared validation utilities for all forms across the application.
 * Each function returns an error string if invalid, or null if valid.
 */

export const validate = {

    required(value: string | undefined | null, fieldName: string = 'This field'): string | null {
        if (!value || !value.trim()) {
            return `${fieldName} is required`;
        }
        return null;
    },

    email(value: string): string | null {
        if (!value || !value.trim()) return null; // Use required() for empty checks
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value.trim())) {
            return 'Please enter a valid email address';
        }
        return null;
    },

    /**
     * UK driving licence number: 16 alphanumeric characters
     * Format: 5 surname chars + 6 DOB digits + 2 initials + 2 check digits + 1 check letter
     */
    ukLicenceNumber(value: string): string | null {
        if (!value || !value.trim()) return null;
        const cleaned = value.replace(/\s/g, '');
        if (cleaned.length !== 16) {
            return 'Licence number must be exactly 16 characters';
        }
        if (!/^[A-Z0-9]{16}$/.test(cleaned)) {
            return 'Licence number must contain only letters and numbers';
        }
        return null;
    },

    /**
     * UK postcode validation
     * Covers all standard UK postcode formats e.g. SW1A 2AA, M1 1AA, B1 1BB
     */
    ukPostcode(value: string): string | null {
        if (!value || !value.trim()) return null;
        const postcodeRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
        if (!postcodeRegex.test(value.trim())) {
            return 'Please enter a valid UK postcode (e.g. SW1A 2AA)';
        }
        return null;
    },

    /**
     * Date of birth: must be in the past, driver must be 16–100 years old
     */
    dateOfBirth(value: string): string | null {
        if (!value) return null;
        const dob = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dob >= today) {
            return 'Date of birth cannot be in the future';
        }

        const ageMs = today.getTime() - dob.getTime();
        const ageYears = ageMs / (1000 * 60 * 60 * 24 * 365.25);

        if (ageYears < 16) {
            return 'Driver must be at least 16 years old';
        }
        if (ageYears > 100) {
            return 'Please enter a valid date of birth';
        }
        return null;
    },

    /**
     * Optional UK phone number
     * Accepts common UK formats:
     * - 07xxx xxxxxx (with leading 0)
     * - 7xxx xxxxxx (without leading 0)
     * - +447xxx xxxxxx (international format)
     * - 447xxx xxxxxx (international without +)
     * - With or without spaces
     */
    ukPhone(value: string): string | null {
        if (!value || !value.trim()) return null; // Phone is optional
        // Make prefix optional: allows 0, 44, +44, or no prefix
        const phoneRegex = /^(\+44\s?|44\s?|0)?(7\d{3}|\d{4})\s?\d{3}\s?\d{3,4}$/;
        if (!phoneRegex.test(value.trim())) {
            return 'Please enter a valid UK phone number';
        }
        return null;
    },

    /**
     * Site code: alphanumeric, hyphens allowed, 2–20 chars
     */
    siteCode(value: string): string | null {
        if (!value || !value.trim()) return null;
        if (value.trim().length < 2) {
            return 'Site code must be at least 2 characters';
        }
        if (value.trim().length > 20) {
            return 'Site code must be 20 characters or less';
        }
        if (!/^[A-Z0-9-]+$/i.test(value.trim())) {
            return 'Site code must contain only letters, numbers, and hyphens';
        }
        return null;
    },

    /**
     * Risk score: must be a number between 0 and 100
     */
    riskScore(value: number | null, label: string): string | null {
        if (value === null || value === undefined) return null;
        if (value < 0 || value > 100) {
            return `${label} must be between 0 and 100`;
        }
        return null;
    },
};

/**
 * Helper: collect all errors from a map of field → error string | null
 * Returns a plain object with only the fields that have errors.
 */
export function collectErrors<T extends Record<string, string | null>>(
    results: T
): Partial<Record<keyof T, string>> {
    const errors: Partial<Record<keyof T, string>> = {};
    for (const key in results) {
        if (results[key] !== null) {
            errors[key as keyof T] = results[key] as string;
        }
    }
    return errors;
}
